/**
 * Life Blueprint V1 · Task 3C-6 B3-C3K-6B2A.
 *
 * ProductionDashboardV299 — AUTHORITATIVE CUSTOMER PRESENTATION.
 *
 * A pure, read-only CONSUMER of an already-validated stored
 * ProductionBlueprintResultV299 (passed as the `report` prop). It renders the
 * authoritative production report exactly as packaged upstream:
 *
 *   01 {t("report.sectionCore")}          (report.core)
 *   02 {t("report.sectionIndices")}        (report.indices)
 *   03 {t("report.sectionCareer")}       (report.career.topIndustry / topRole)
 *   04 个人策略 · Sheet13        (report.strategy)
 *   05 年度建议 · Sheet12        (report.annualAdvice)
 *   06 发展方位 · Sheet15        (report.directionAdvice)
 *   07 号码使用建议 · Sheet24    (report.numberAdvice)
 *   08 90天行动计划 · Sheet14    (report.actionPlan90Day)
 *   09 财富行动蓝图 · Sheet25    (report.wealthBlueprint)
 *
 * ISOLATION GUARANTEES (PRESENTATION ONLY — NO BUSINESS CALCULATION)
 *   - Imports NO calculation/orchestration/adapter modules (no
 *     production_application, production_report_adapter, orchestrators, Sheet
 *     engines, productionCoreUiAdapter, blueprintEngine). Recalculates nothing.
 *   - Reads ONLY from `report`. No storage/localStorage, no system clock, no
 *     network. Does not mutate the report.
 *   - Uses explicit customer-facing field whitelists. Never recursively dumps
 *     objects; never exposes metadata/inputs/derived/orderedSlots/helper/QC.
 *   - Does NOT synthesize legacy summaryCards / overviewMetrics / themes; does
 *     NOT split/parse Sheet24 numberAdvice into favorable/unfavorable arrays;
 *     does NOT compress Sheet25 wealthBlueprint into focus/priority/watch/next.
 *   - No geographic / direction mapping. Sheet15 direction strings are rendered
 *     verbatim and never reinterpreted via profile.region.
 *   - Indices/careers/capabilities are rendered in the authoritative supplied
 *     order; nothing is sorted, ranked, averaged, or re-thresholded in the UI.
 */
import { ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { localizeReportValue } from "@/i18n/reportValueCatalog";
import { localizeReportNarrative } from "@/i18n/reportNarrativeCatalog";
import { localizeNumberAdviceValue } from "@/i18n/reportNumberAdviceCatalog";
import { localizeWealthCapabilityAction } from "@/i18n/reportWealthBlueprintCatalog";
import {
  presentSheet25WealthPersonality,
  presentSheet25IncomeTendency,
  presentSheet25IncomeStructureAdvice,
  presentSheet25StrongestSummary,
  presentSheet25PriorityReinforcement,
  presentSheet25StageConclusion,
  presentSheet25RecommendedModeDetail,
  presentSheet25AnnualRhythmDetail,
  presentSheet25GuiRenResource,
  presentSheet25IntegratedPriority,
  presentSheet25RiskToleranceDetail,
  presentSheet25Day30Plan,
  presentSheet25Day60Plan,
  presentSheet25Day90Plan,
  presentSheet25NumberCulture,
} from "@/i18n/reportWealthBlueprintPresenter";
import {
  presentSheet24CoreBasis,
  presentSheet24RuleDigest,
  presentSheet24CombinationStructure,
  presentSheet24SummaryUsage,
  presentSheet24IntegratedRecommendation,
} from "@/i18n/reportNumberAdvicePresenter";
import {
  presentSheet13SummaryAdvice,
  presentSheet15WealthAdvice,
  presentSheet15IntegratedConclusion,
} from "@/i18n/reportCompositePresenter";

/* ── Local presentational helpers (no business logic) ─────────────────── */

const canonicalizeProfileGenderForPresentation = (value) => {
  if (value === "female") return "女";
  if (value === "male") return "男";
  return value;
};

const PROFILE_REGION_PRESENTATION_ALIASES = Object.freeze({
  "CN-EAST": "华东地区",
  "CN-NORTH": "华北地区",
  "CN-SOUTH": "华南地区",
  "CN-CENTRAL": "华中地区",
  "CN-SOUTHWEST": "西南地区",
  "CN-NORTHWEST": "西北地区",
  "CN-NORTHEAST": "东北地区",
  "CN-HKMOTW": "港澳台地区",
  OVERSEAS: "海外地区",
});

const canonicalizeProfileRegionForPresentation = (value) =>
  PROFILE_REGION_PRESENTATION_ALIASES[value] ?? value;

const Field = ({ label, value, testid }) => (
  <div className="flex flex-col gap-1" data-testid={testid}>
    <span className="text-[10px] tracking-widest uppercase font-mono text-emerald-300/70">{label}</span>
    <span className="text-sm text-slate-100 break-words">{value}</span>
  </div>
);

const TextBlock = ({ label, value, testid }) =>
  value == null || value === "" ? null : (
    <div className="flex flex-col gap-1.5" data-testid={testid}>
      <span className="text-[10px] tracking-widest uppercase font-mono text-emerald-300/70">{label}</span>
      <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line break-words">{value}</p>
    </div>
  );

const ReportSection = ({ index, title, children, testid }) => (
  <section data-testid={testid} className="card-premium rounded-2xl p-6 space-y-5">
    <div className="flex items-baseline gap-3">
      <span className="text-xs font-mono tracking-widest text-emerald-300/60">{index}</span>
      <h3 className="font-serif-cn text-emerald-100 text-base md:text-lg">{title}</h3>
    </div>
    {children}
  </section>
);

const MetricCard = ({ label, score, status, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-emerald-400/25 bg-navy-800/50 p-4 flex flex-col gap-2">
    <div className="flex items-baseline justify-between">
      <span className="text-sm text-slate-100">{label}</span>
      <span className="text-lg font-mono text-emerald-200" data-testid={`${testid}-score`}>{score}</span>
    </div>
    <div className="h-1.5 rounded-full bg-navy-900/70 overflow-hidden">
      <div className="h-full rounded-full bg-emerald-400/70" style={{ width: `${score}%` }} />
    </div>
    {status ? (
      <span className="text-[11px] font-mono tracking-widest text-emerald-300/70" data-testid={`${testid}-status`}>{status}</span>
    ) : null}
  </div>
);

const RankRow = ({ rank, name, score, testid }) => (
  <div data-testid={testid} className="flex items-center gap-3 rounded-lg border border-emerald-400/20 bg-navy-800/40 px-4 py-2.5">
    <span className="text-xs font-mono text-emerald-300/70 w-6 flex-shrink-0" data-testid={`${testid}-rank`}>#{rank}</span>
    <span className="text-sm text-slate-100 flex-1 break-words" data-testid={`${testid}-name`}>{name}</span>
    <span className="text-sm font-mono text-emerald-200 flex-shrink-0" data-testid={`${testid}-score`}>{score}</span>
  </div>
);

const ActionPhase = ({ phase, theme, goal, metric, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-emerald-400/25 bg-navy-800/50 p-4 space-y-2.5">
    <div className="flex items-center gap-2">
      <span className="text-xs font-mono tracking-widest text-emerald-300/80">{phase}</span>
      <span className="text-sm text-emerald-100" data-testid={`${testid}-theme`}>{theme}</span>
    </div>
    <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line break-words" data-testid={`${testid}-goal`}>{goal}</p>
    <p className="text-xs text-slate-400 leading-relaxed whitespace-pre-line break-words" data-testid={`${testid}-metric`}>{metric}</p>
  </div>
);

const CapabilityRow = ({ label, score, level, action, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-emerald-400/25 bg-navy-800/50 p-4 space-y-2">
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-100">{label}</span>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-lg font-mono text-emerald-200" data-testid={`${testid}-score`}>{score}</span>
        <span className="text-[11px] font-mono tracking-widest text-emerald-300/70" data-testid={`${testid}-level`}>{level}</span>
      </div>
    </div>
    <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-line break-words" data-testid={`${testid}-action`}>{action}</p>
  </div>
);

/* ── Authoritative customer-facing whitelists (documented, no inference) ── */

// {t("report.sectionIndices")} — authoritative six keys + recommended Chinese labels.
const INDEX_ROWS = [
  ["careerFit", "report.indexCareerFit"],
  ["wealth", "report.indexWealth"],
  ["venture", "report.indexVenture"],
  ["learning", "report.indexLearning"],
  ["network", "report.indexNetwork"],
  ["stability", "report.indexStability"],
];

// 个人策略 · Sheet13 — ordered customer-facing string fields.
const STRATEGY_FIELDS = [
  ["corePosition", "report.strategyCorePosition"],
  ["corePositionExplanation", "report.strategyCorePositionExplanation"],
  ["careerStrategy", "report.strategyCareerStrategy"],
  ["careerAction", "report.strategyCareerAction"],
  ["wealthStrategy", "report.strategyWealthStrategy"],
  ["wealthAction", "report.strategyWealthAction"],
  ["ventureFeasibility", "report.strategyVentureFeasibility"],
  ["learningGrowth", "report.strategyLearningGrowth"],
  ["mentorCooperation", "report.strategyMentorCooperation"],
  ["stableLife", "report.strategyStableLife"],
  ["annualFocus", "report.strategyAnnualFocus"],
  ["riskAlert", "report.strategyRiskAlert"],
  ["responseAdvice", "report.strategyResponseAdvice"],
  ["avoidItems", "report.strategyAvoidItems"],
  ["summaryAdvice", "report.strategySummaryAdvice"],
];

// 年度建议 · Sheet12 — ordered customer-facing string fields.
const ANNUAL_FIELDS = [
  ["annualTitle", "report.annualTitle"],
  ["suitableActions", "report.annualSuitableActions"],
  ["actionsToAvoid", "report.annualActionsToAvoid"],
  ["annualCareerAdvice", "report.annualCareerAdvice"],
  ["annualWealthRhythm", "report.annualWealthRhythm"],
  ["annualLearningAdvice", "report.annualLearningAdvice"],
  ["annualRelationshipAdvice", "report.annualRelationshipAdvice"],
  ["annualVentureCareerAdvice", "report.annualVentureCareerAdvice"],
  ["annualActionList", "report.annualActionList"],
  ["weaknessDescription", "report.annualWeaknessDescription"],
  ["elementCareerAdvice", "report.annualElementCareerAdvice"],
  ["elementWealthAdvice", "report.annualElementWealthAdvice"],
];

// 发展方位 · Sheet15 — direction strings rendered verbatim (no geo mapping).
const DIRECTION_FIELDS = [
  ["topDevelopmentDirection", "report.directionTopDevelopment"],
  ["secondaryDevelopmentDirection", "report.directionSecondaryDevelopment"],
  ["reduceDevelopmentDirection", "report.directionReduceDevelopment"],
  ["primaryDirection", "report.directionPrimary"],
  ["secondaryDirection", "report.directionSecondary"],
  ["reduceDirection", "report.directionReduce"],
  ["recommendedIndustries", "report.directionRecommendedIndustries"],
  ["recommendedRoles", "report.directionRecommendedRoles"],
  ["suitableEnvironment", "report.directionSuitableEnvironment"],
  ["unsuitableEnvironment", "report.directionUnsuitableEnvironment"],
  ["careerAdvice", "report.directionCareerAdvice"],
  ["wealthAdvice", "report.directionWealthAdvice"],
  ["ventureAdvice", "report.directionVentureAdvice"],
  ["parallelWarning", "report.directionParallelWarning"],
  ["integratedConclusion", "report.directionIntegratedConclusion"],
];

const C3B1_DIRECTION_FINITE_KEYS = new Set([
  "topDevelopmentDirection",
  "secondaryDevelopmentDirection",
  "reduceDevelopmentDirection",
  "primaryDirection",
  "secondaryDirection",
  "reduceDirection",
  "recommendedIndustries",
  "recommendedRoles",
  "suitableEnvironment",
  "unsuitableEnvironment",
  "careerAdvice",
  "ventureAdvice",
  "parallelWarning",
]);

// 号码使用建议 · Sheet24 — customer-facing STRING blocks (never split/parsed).
const NUMBER_FIELDS = [
  ["coreBasis", "report.numberCoreBasis"],
  ["elementMeaning", "report.numberElementMeaning"],
  ["lifeNumberRaw", "report.numberLifeNumberRaw"],
  ["lifeNumberElement", "report.numberLifeElement"],
  ["lifeNumberSummary", "report.numberLifeSummary"],
  ["ruleDigest", "report.numberRuleDigest"],
  ["overallRelation", "report.numberOverallRelation"],
  ["boundaryHint", "report.numberBoundaryHint"],
  ["coreNumbersBlock", "report.numberCoreBlock"],
  ["auxNumbersBlock", "report.numberAuxBlock"],
  ["lifeNumberBlock", "report.numberLifeBlock"],
  ["combinationStructure", "report.numberCombinationStructure"],
  ["priorityOrder", "report.numberPriorityOrder"],
  ["reinforcementLogic", "report.numberReinforcementLogic"],
  ["lifeNumberUsage", "report.numberLifeUsage"],
  ["reduceNumbersBlock", "report.numberReduceBlock"],
  ["reduceRelation", "report.numberReduceRelation"],
  ["lifeNumberOverlap", "report.numberLifeOverlap"],
  ["reduceBoundary", "report.numberReduceBoundary"],
  ["mobileAdvice", "report.numberMobileAdvice"],
  ["vehicleAdvice", "report.numberVehicleAdvice"],
  ["doorAdvice", "report.numberDoorAdvice"],
  ["accountAdvice", "report.numberAccountAdvice"],
  ["summaryUsage", "report.numberSummaryUsage"],
  ["integratedRecommendation", "report.numberIntegratedRecommendation"],
];

const C3C1_NUMBER_EXACT_KEYS = new Set([
  "elementMeaning",
  "lifeNumberElement",
  "lifeNumberSummary",
  "overallRelation",
  "boundaryHint",
  "coreNumbersBlock",
  "auxNumbersBlock",
  "lifeNumberBlock",
  "priorityOrder",
  "reinforcementLogic",
  "lifeNumberUsage",
  "reduceNumbersBlock",
  "reduceRelation",
  "lifeNumberOverlap",
  "reduceBoundary",
  "mobileAdvice",
  "vehicleAdvice",
  "doorAdvice",
  "accountAdvice",
]);

// 财富行动蓝图 · Sheet25 — authoritative user-facing outputs (no compression).
const WEALTH_TEXT_FIELDS = [
  ["wealthPersonality", "report.wealthPersonality"],
  ["incomeTendency", "report.wealthIncomeTendency"],
  ["incomeStructureAdvice", "report.wealthIncomeStructureAdvice"],
  ["strongestSummary", "report.wealthStrongestSummary"],
  ["priorityReinforcement", "report.wealthPriorityReinforcement"],
  ["stageConclusion", "report.wealthStageConclusion"],
  ["recommendedModeDetail", "report.wealthRecommendedModeDetail"],
  ["annualRhythmDetail", "report.wealthAnnualRhythmDetail"],
  ["guiRenResource", "report.wealthGuiRenResource"],
  ["riskToleranceDetail", "report.wealthRiskToleranceDetail"],
  ["day30Plan", "report.wealthDay30Plan"],
  ["day60Plan", "report.wealthDay60Plan"],
  ["day90Plan", "report.wealthDay90Plan"],
  ["numberCulture", "report.wealthNumberCulture"],
  ["integratedPriority", "report.wealthIntegratedPriority"],
];

export default function ProductionDashboardV299({ report, onEdit }) {
  const { t, locale } = useI18n();
  const profile = report.profile || {};
  const businessContext = report.businessContext || {};
  const core = report.core || {};
  const indices = (report.indices && report.indices.indices) || {};
  const statuses = (report.indices && report.indices.statuses) || {};
  const topIndustry = (report.career && report.career.topIndustry) || {};
  const topRole = (report.career && report.career.topRole) || {};
  const strategy = report.strategy || {};
  const annual = report.annualAdvice || {};
  const direction = report.directionAdvice || {};
  const number = report.numberAdvice || {};
  const plan = report.actionPlan90Day || {};
  const wealth = report.wealthBlueprint || {};

  const industryTop5 = Array.isArray(topIndustry.top5) ? topIndustry.top5 : [];
  const roleTop5 = Array.isArray(topRole.top5) ? topRole.top5 : [];
  const fourPillars = Array.isArray(core.fourPillars) ? core.fourPillars : [];

  const strategySummaryAdvice = presentSheet13SummaryAdvice(
    locale,
    strategy.summaryAdvice,
    {
      currentGoal: businessContext.currentGoal,
      top1Industry: industryTop5[0]?.name,
      top1Role: roleTop5[0]?.name,
    },
  );

  const directionWealthAdvice = presentSheet15WealthAdvice(
    locale,
    direction.wealthAdvice,
    {
      currentGoal: businessContext.currentGoal,
      fiveElementNeed: core.neededElement,
    },
  );

  const directionIntegratedConclusion =
    presentSheet15IntegratedConclusion(
      locale,
      direction.integratedConclusion,
      {
        dayMasterElement: core.dayMasterElement,
        dayMasterStatus: core.dayMasterStatus,
        fiveElementNeed: core.neededElement,
        topDevelopmentDirection: direction.topDevelopmentDirection,
        secondaryDevelopmentDirection:
          direction.secondaryDevelopmentDirection,
        recommendedIndustries: direction.recommendedIndustries,
        recommendedRoles: direction.recommendedRoles,
        reduceDevelopmentDirection:
          direction.reduceDevelopmentDirection,
        unsuitableEnvironment: direction.unsuitableEnvironment,
      },
    );

  const numberCoreBasis =
    presentSheet24CoreBasis(
      locale,
      number.coreBasis,
      {
        fiveElementNeed: number.inputs?.fiveElementNeed,
        dayMasterStem: number.inputs?.dayMasterStem,
        dayMasterElement: number.inputs?.dayMasterElement,
      },
    );

  const numberRuleDigest =
    presentSheet24RuleDigest(
      locale,
      number.ruleDigest,
      {
        r5CoreNumbers: number.r5CoreNumbers,
        r7AuxNumbers: number.r7AuxNumbers,
        r3LifeNumber: number.r3LifeNumber,
      },
    );

  const numberCombinationStructure =
    presentSheet24CombinationStructure(
      locale,
      number.combinationStructure,
      {
        r5CoreNumbers: number.r5CoreNumbers,
        r7AuxNumbers: number.r7AuxNumbers,
        r3LifeNumber: number.r3LifeNumber,
        r4LifeNumberElement:
          number.r4LifeNumberElement,
        r8ControllingElement:
          number.r8ControllingElement,
      },
    );

  const numberSummaryUsage =
    presentSheet24SummaryUsage(
      locale,
      number.summaryUsage,
      {
        r5CoreNumbers: number.r5CoreNumbers,
        r7AuxNumbers: number.r7AuxNumbers,
        r3LifeNumber: number.r3LifeNumber,
      },
    );

  const numberIntegratedRecommendation =
    presentSheet24IntegratedRecommendation(
      locale,
      number.integratedRecommendation,
      {
        r2Element: number.r2Element,
        r3LifeNumber: number.r3LifeNumber,
        r4LifeNumberElement:
          number.r4LifeNumberElement,
        r5CoreNumbers: number.r5CoreNumbers,
        r6GeneratingElement:
          number.r6GeneratingElement,
        r7AuxNumbers: number.r7AuxNumbers,
        r8ControllingElement:
          number.r8ControllingElement,
        r9ReduceNumbers: number.r9ReduceNumbers,
      },
    );

  const wealthInputs = wealth.inputs || {};
  const wealthDerived = wealth.derived || {};

  const wealthCapabilityScores =
    Array.isArray(wealthDerived.capabilityScores)
      ? wealthDerived.capabilityScores
      : [];

  const wealthMaxScore =
    Number.isInteger(wealthDerived.maxIndex)
      ? wealthCapabilityScores[wealthDerived.maxIndex]
      : undefined;

  const wealthMinScore =
    Number.isInteger(wealthDerived.minIndex)
      ? wealthCapabilityScores[wealthDerived.minIndex]
      : undefined;

  const wealthD2Presented = {
    wealthPersonality: presentSheet25WealthPersonality(
      locale,
      wealth.wealthPersonality,
      {
        lifeNumber: wealthInputs.lifeNumber,
        corePosition: wealthInputs.corePosition,
        positionSummary: wealthInputs.positionSummary,
      },
    ),

    incomeTendency: presentSheet25IncomeTendency(
      locale,
      wealth.incomeTendency,
      {
        stableIncomeScore: wealth.q18StableIncome,
        opportunityIncomeScore: wealth.q19OpportunityIncome,
        incomeStable: wealthDerived.incomeStable,
      },
    ),

    incomeStructureAdvice: presentSheet25IncomeStructureAdvice(
      locale,
      wealth.incomeStructureAdvice,
      {
        currentGoal: wealthInputs.currentGoal,
        topIndustry: wealthInputs.topIndustry,
        topRole: wealthInputs.topRole,
        incomeStable: wealthDerived.incomeStable,
      },
    ),

    strongestSummary: presentSheet25StrongestSummary(
      locale,
      wealth.strongestSummary,
      {
        maxLabel: wealthDerived.maxLabel,
        maxScore: wealthMaxScore,
      },
    ),

    priorityReinforcement: presentSheet25PriorityReinforcement(
      locale,
      wealth.priorityReinforcement,
      {
        minLabel: wealthDerived.minLabel,
        minScore: wealthMinScore,
      },
    ),

    stageConclusion: presentSheet25StageConclusion(
      locale,
      wealth.stageConclusion,
      {
        average: wealthDerived.average,
        minLabel: wealthDerived.minLabel,
        maxLabel: wealthDerived.maxLabel,
      },
    ),

    recommendedModeDetail: presentSheet25RecommendedModeDetail(
      locale,
      wealth.recommendedModeDetail,
      {
        recommendedMode: wealth.q21RecommendedMode,
        topIndustry: wealthInputs.topIndustry,
        topRole: wealthInputs.topRole,
      },
    ),

    annualRhythmDetail: presentSheet25AnnualRhythmDetail(
      locale,
      wealth.annualRhythmDetail,
      {
        personalYear: wealthInputs.personalYear,
        annualTitle: wealthInputs.annualTitle,
        annualWealthRhythm: wealthInputs.annualWealthRhythm,
      },
    ),

    guiRenResource: presentSheet25GuiRenResource(
      locale,
      wealth.guiRenResource,
      {
        guiRenScore: wealth.n5GuiRen,
        annualFocus: wealthInputs.annualFocus,
      },
    ),

    integratedPriority: presentSheet25IntegratedPriority(
      locale,
      wealth.integratedPriority,
      {
        minLabel: wealthDerived.minLabel,
        maxLabel: wealthDerived.maxLabel,
      },
    ),
  };

  const wealthSheet14 =
    wealthInputs.sheet14Sections || {};

  const wealthSheet24 =
    wealthInputs.sheet24Helpers || {};

  const wealthD3Presented = {
    riskToleranceDetail: presentSheet25RiskToleranceDetail(
      locale,
      wealth.riskToleranceDetail,
      {
        riskControlScore: wealth.q13RiskControl,
        stabilityScore: wealth.q14Stability,
        dayMasterStatusText:
          wealthSheet14.dayMasterStatusText,
        annualRiskNote:
          wealthSheet14.annualRiskNote,
      },
    ),

    day30Plan: presentSheet25Day30Plan(
      locale,
      wealth.day30Plan,
      {
        minLabel: wealthDerived.minLabel,
        day30GoalLink:
          wealthSheet14.day30GoalLink,
        day30Metric:
          wealthSheet14.day30Metric,
      },
    ),

    day60Plan: presentSheet25Day60Plan(
      locale,
      wealth.day60Plan,
      {
        recommendedMode:
          wealth.q21RecommendedMode,
        day60GoalLink:
          wealthSheet14.day60GoalLink,
        day60Metric:
          wealthSheet14.day60Metric,
      },
    ),

    day90Plan: presentSheet25Day90Plan(
      locale,
      wealth.day90Plan,
      {
        day90GoalLink:
          wealthSheet14.day90GoalLink,
        day90Metric:
          wealthSheet14.day90Metric,
      },
    ),

    numberCulture: presentSheet25NumberCulture(
      locale,
      wealth.numberCulture,
      {
        r5CoreNumbers:
          wealthSheet24.r5CoreNumbers,
        r7AuxNumbers:
          wealthSheet24.r7AuxNumbers,
        r3LifeNumber:
          wealthSheet24.r3LifeNumber,
        r9ReduceNumbers:
          wealthSheet24.r9ReduceNumbers,
        r10RelationClass:
          wealthSheet24.r10RelationClass,
      },
    ),
  };

  const wealthCaps = [1, 2, 3, 4, 5].map((n) => ({
    n,
    label: wealth[`cap${n}Label`],
    score: wealth[`cap${n}Score`],
    level: wealth[`cap${n}Level`],
    action: localizeWealthCapabilityAction(
      locale,
      `cap${n}Action`,
      wealth[`cap${n}Action`],
    ),
  }));

  return (
    <section data-testid="production-dashboard-v299" className="space-y-8">
      {/* Profile */}
      <div data-testid="v299-profile" className="card-premium rounded-2xl p-6">
        <div className="text-xs tracking-widest uppercase font-mono text-emerald-300/80 mb-4">
          {t("report.profileTitle")}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Field label={t("report.name")} value={profile.name || "—"} testid="v299-profile-name" />
          <Field label={t("report.birthDate")} value={profile.birthDateISO || "—"} testid="v299-profile-birthDate" />
          <Field label={t("report.gender")} value={localizeReportValue(locale, "gender", canonicalizeProfileGenderForPresentation(profile.gender)) || "—"} testid="v299-profile-gender" />
          <Field label={t("report.region")} value={localizeReportValue(locale, "region", canonicalizeProfileRegionForPresentation(profile.region)) || "—"} testid="v299-profile-region" />
          {profile.birthTimeExact ? (
            <Field label={t("report.exactBirthTime")} value={profile.birthTimeExact} testid="v299-profile-birthTimeExact" />
          ) : null}
        </div>
      </div>

      {/* Business context */}
      <div data-testid="v299-business-context" className="card-premium rounded-2xl p-6">
        <div className="text-xs tracking-widest uppercase font-mono text-emerald-300/80 mb-4">
          {t("report.planningContext")}
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("report.currentGoal")} value={localizeReportValue(locale, "currentGoal", businessContext.currentGoal) || "—"} testid="v299-business-currentGoal" />
          <Field label={t("report.referenceYear")} value={String(businessContext.referenceYear ?? "—")} testid="v299-business-referenceYear" />
        </div>
      </div>

      {/* 01 {t("report.sectionCore")} */}
      <ReportSection index="01" title={t("report.sectionCore")} testid="v299-section-core">
        {core.fullBazi ? (
          <Field label={t("report.fourPillars")} value={core.fullBazi} testid="v299-core-fullBazi" />
        ) : null}
        {fourPillars.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="v299-core-pillars">
            {fourPillars.map((p, i) => (
              <div key={i} data-testid={`v299-core-pillar-${i}`} className="rounded-xl border border-emerald-400/25 bg-navy-800/50 px-4 py-3 text-center">
                <div className="text-[10px] font-mono tracking-widest text-emerald-300/70">{localizeReportValue(locale, "pillarLabel", p.label)}</div>
                <div className="text-base text-slate-100 mt-1">{p.ganZhi}</div>
              </div>
            ))}
          </div>
        ) : null}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Field label={t("report.dayMaster")} value={localizeReportValue(locale, "element", core.dayMasterElement) || "—"} testid="v299-core-dayMasterElement" />
          <Field label={t("report.dayMasterStrength")} value={localizeReportValue(locale, "dayMasterStatus", core.dayMasterStatus) || "—"} testid="v299-core-dayMasterStatus" />
          <Field label={t("report.strengthScore")} value={String(core.strengthScore ?? "—")} testid="v299-core-strengthScore" />
          <Field label={t("report.neededElement")} value={localizeReportValue(locale, "fiveElementNeed", core.neededElement) || "—"} testid="v299-core-neededElement" />
          <Field label={t("report.seasonalSupport")} value={localizeReportValue(locale, "supportState", core.seasonSupport) || "—"} testid="v299-core-seasonSupport" />
          <Field label={t("report.rootSupport")} value={localizeReportValue(locale, "supportState", core.groundSupport) || "—"} testid="v299-core-groundSupport" />
          <Field label={t("report.peerSupport")} value={localizeReportValue(locale, "supportState", core.peerSupport) || "—"} testid="v299-core-peerSupport" />
        </div>
        {core.boundaryWarning ? (
          <p className="text-xs text-amber-300/90 leading-relaxed" data-testid="v299-core-boundaryWarning">
            {localizeReportValue(locale, "boundaryWarning", core.boundaryWarning)}
          </p>
        ) : null}
      </ReportSection>

      {/* 02 {t("report.sectionIndices")} */}
      <ReportSection index="02" title={t("report.sectionIndices")} testid="v299-section-indices">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {INDEX_ROWS.map(([key, labelKey]) => (
            <MetricCard
              key={key}
              label={t(labelKey)}
              score={indices[key]}
              status={localizeReportValue(locale, "indexStatus", statuses[key])}
              testid={`v299-index-${key}`}
            />
          ))}
        </div>
      </ReportSection>

      {/* 03 {t("report.sectionCareer")} */}
      <ReportSection index="03" title={t("report.sectionCareer")} testid="v299-section-career">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2.5" data-testid="v299-career-topIndustry">
            <div className="text-xs tracking-widest uppercase font-mono text-emerald-300/70">{t("report.topIndustries")}</div>
            {industryTop5.map((row) => (
              <RankRow key={row.rank} rank={row.rank} name={localizeReportValue(locale, "industry", row.name)} score={row.score} testid={`v299-industry-${row.rank}`} />
            ))}
          </div>
          <div className="space-y-2.5" data-testid="v299-career-topRole">
            <div className="text-xs tracking-widest uppercase font-mono text-emerald-300/70">{t("report.topRoles")}</div>
            {roleTop5.map((row) => (
              <RankRow key={row.rank} rank={row.rank} name={localizeReportValue(locale, "role", row.name)} score={row.score} testid={`v299-role-${row.rank}`} />
            ))}
          </div>
        </div>
      </ReportSection>

      {/* 04 个人策略 · Sheet13 */}
      <ReportSection index="04" title={t("report.sectionStrategy")} testid="v299-section-strategy">
        <div className="grid sm:grid-cols-2 gap-5">
          {STRATEGY_FIELDS.map(([key, labelKey]) => (
            <TextBlock
              key={key}
              label={t(labelKey)}
              value={
                key === "summaryAdvice"
                  ? strategySummaryAdvice
                  : localizeReportNarrative(locale, "sheet13", strategy[key])
              }
              testid={`v299-strategy-${key}`}
            />
          ))}
        </div>
      </ReportSection>

      {/* 05 年度建议 · Sheet12 */}
      <ReportSection index="05" title={t("report.sectionAnnual")} testid="v299-section-annualAdvice">
        <div className="grid sm:grid-cols-2 gap-5">
          {ANNUAL_FIELDS.map(([key, labelKey]) => (
            <TextBlock
              key={key}
              label={t(labelKey)}
              value={localizeReportNarrative(locale, "sheet12", annual[key])}
              testid={`v299-annual-${key}`}
            />
          ))}
        </div>
      </ReportSection>

      {/* 06 发展方位 · Sheet15 */}
      <ReportSection index="06" title={t("report.sectionDirection")} testid="v299-section-directionAdvice">
        <div className="grid sm:grid-cols-2 gap-5">
          {DIRECTION_FIELDS.map(([key, labelKey]) => (
            <TextBlock
              key={key}
              label={t(labelKey)}
              value={
                C3B1_DIRECTION_FINITE_KEYS.has(key)
                  ? localizeReportNarrative(locale, "sheet15", direction[key])
                  : key === "wealthAdvice"
                    ? directionWealthAdvice
                    : key === "integratedConclusion"
                      ? directionIntegratedConclusion
                      : direction[key]
              }
              testid={`v299-direction-${key}`}
            />
          ))}
        </div>
      </ReportSection>

      {/* 07 号码使用建议 · Sheet24 */}
      <ReportSection index="07" title={t("report.sectionNumber")} testid="v299-section-numberAdvice">
        <div className="grid sm:grid-cols-2 gap-5">
          {NUMBER_FIELDS.map(([key, labelKey]) => (
            <TextBlock
              key={key}
              label={t(labelKey)}
              value={
                C3C1_NUMBER_EXACT_KEYS.has(key)
                  ? localizeNumberAdviceValue(
                      locale,
                      key,
                      number[key],
                    )
                  : key === "coreBasis"
                    ? numberCoreBasis
                    : key === "ruleDigest"
                      ? numberRuleDigest
                      : key === "combinationStructure"
                        ? numberCombinationStructure
                        : key === "summaryUsage"
                          ? numberSummaryUsage
                          : key === "integratedRecommendation"
                            ? numberIntegratedRecommendation
                            : number[key]
              }
              testid={`v299-number-${key}`}
            />
          ))}
        </div>
      </ReportSection>

      {/* 08 90天行动计划 · Sheet14 */}
      <ReportSection index="08" title={t("report.sectionActionPlan")} testid="v299-section-actionPlan90Day">
        <div className="grid sm:grid-cols-2 gap-5 mb-1">
          <Field label={t("report.personalYear")} value={String(plan.personalYear ?? "—")} testid="v299-plan-personalYear" />
          <Field label={t("report.actionCurrentGoal")} value={localizeReportValue(locale, "currentGoal", plan.currentGoal) || "—"} testid="v299-plan-currentGoal" />
        </div>
        {plan.currentGoal === "综合分析" &&
        plan.day30Theme === "—" &&
        plan.day30GoalLink === "—" &&
        plan.day30Metric === "—" &&
        plan.day60Theme === "—" &&
        plan.day60GoalLink === "—" &&
        plan.day60Metric === "—" &&
        plan.day90Theme === "—" &&
        plan.day90GoalLink === "—" &&
        plan.day90Metric === "—" ? (
          <p
            className="text-sm text-slate-300 leading-relaxed"
            data-testid="v299-plan-sourceUnavailable"
          >
            {t("report.actionPlanSourceUnavailable")}
          </p>
        ) : null}
        <div className="grid md:grid-cols-3 gap-4">
          <ActionPhase
            phase={t("report.phase30")}
            theme={localizeReportNarrative(locale, "sheet14", plan.day30Theme)}
            goal={localizeReportNarrative(locale, "sheet14", plan.day30GoalLink)}
            metric={localizeReportNarrative(locale, "sheet14", plan.day30Metric)}
            testid="v299-plan-day30"
          />
          <ActionPhase
            phase={t("report.phase60")}
            theme={localizeReportNarrative(locale, "sheet14", plan.day60Theme)}
            goal={localizeReportNarrative(locale, "sheet14", plan.day60GoalLink)}
            metric={localizeReportNarrative(locale, "sheet14", plan.day60Metric)}
            testid="v299-plan-day60"
          />
          <ActionPhase
            phase={t("report.phase90")}
            theme={localizeReportNarrative(locale, "sheet14", plan.day90Theme)}
            goal={localizeReportNarrative(locale, "sheet14", plan.day90GoalLink)}
            metric={localizeReportNarrative(locale, "sheet14", plan.day90Metric)}
            testid="v299-plan-day90"
          />
        </div>
        <TextBlock
          label={t("report.dayMasterNote")}
          value={localizeReportNarrative(locale, "sheet14", plan.dayMasterStatusText)}
          testid="v299-plan-dayMasterStatusText"
        />
        <TextBlock
          label={t("report.annualRiskNote")}
          value={localizeReportNarrative(locale, "sheet14", plan.annualRiskNote)}
          testid="v299-plan-annualRiskNote"
        />
      </ReportSection>

      {/* 09 财富行动蓝图 · Sheet25 */}
      <ReportSection index="09" title={t("report.sectionWealth")} testid="v299-section-wealthBlueprint">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="v299-wealth-capabilities">
          {wealthCaps.map((cap) => (
            <CapabilityRow
              key={cap.n}
              label={localizeReportValue(locale, "wealthCapability", cap.label)}
              score={cap.score}
              level={localizeReportValue(locale, "wealthCapabilityLevel", cap.level)}
              action={cap.action}
              testid={`v299-wealth-cap${cap.n}`}
            />
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          {WEALTH_TEXT_FIELDS.map(([key, labelKey]) => (
            <TextBlock
              key={key}
              label={t(labelKey)}
              value={
                Object.prototype.hasOwnProperty.call(
                  wealthD2Presented,
                  key,
                )
                  ? wealthD2Presented[key]
                  : Object.prototype.hasOwnProperty.call(
                      wealthD3Presented,
                      key,
                    )
                    ? wealthD3Presented[key]
                    : wealth[key]
              }
              testid={`v299-wealth-${key}`}
            />
          ))}
        </div>
      </ReportSection>

      {onEdit ? (
        <div>
          <button
            type="button"
            data-testid="v299-edit-profile-button"
            onClick={onEdit}
            className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs inline-flex items-center gap-1.5"
          >
            {t("report.editProfile")} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null}
    </section>
  );
}
